// StepIdentity — Phase 5 onboarding wizard, Step 1.
//
// Captures the legal identity of the new tenant:
//   • Bilingual name (AR + EN)
//   • Optional trade name
//   • CR (commercial registration) number
//   • VAT number (15-digit Saudi standard validated softly here)
//   • Primary contact (phone / email)
//   • National-address fields (street, district, city, postal code, …)
//
// All validation is soft (warn-only) — onboarding must NEVER block on
// missing optional fields. The only HARD requirement is that one of
// name_ar / name_en is present so the company has SOMETHING to call
// itself by. The backend enforces the same rule in
// `_build_company_profile_payload`.
import React, { useState, useEffect } from 'react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Building2, ChevronRight, AlertTriangle } from 'lucide-react';


const FIELD_GROUPS = [
  {
    title: 'Legal identity',
    description: 'Used on invoices, ZATCA filings and the audit trail.',
    fields: [
      { key: 'name_ar',     label: 'Company name (Arabic)',  placeholder: 'شركة ...',          type: 'text', dir: 'rtl' },
      { key: 'name_en',     label: 'Company name (English)', placeholder: 'ACME Contracting',  type: 'text' },
      { key: 'trade_name',  label: 'Trade name (optional)',  placeholder: 'ACME',              type: 'text' },
      { key: 'cr_number',   label: 'CR number',              placeholder: '1010XXXXXX',        type: 'text' },
      { key: 'vat_number',  label: 'VAT number',             placeholder: '3 + 14 digits',     type: 'text', maxLength: 15 },
    ],
  },
  {
    title: 'Primary contact',
    description: 'Used as the default email for outbound documents.',
    fields: [
      { key: 'email',       label: 'Email',     placeholder: 'info@company.com', type: 'email' },
      { key: 'phone',       label: 'Phone',     placeholder: '+9665XXXXXXXX',    type: 'tel' },
      { key: 'website',     label: 'Website (optional)', placeholder: 'https://…', type: 'url' },
    ],
  },
];

const ADDRESS_FIELDS = [
  { key: 'building_number',   label: 'Building #' },
  { key: 'street',            label: 'Street' },
  { key: 'additional_number', label: 'Additional #' },
  { key: 'district',          label: 'District' },
  { key: 'city',              label: 'City' },
  { key: 'postal_code',       label: 'Postal code' },
];


// Soft KSA VAT validator: 15 digits, starts and ends with '3'.
function softVatNote(v) {
  if (!v) return null;
  const s = String(v).replace(/\D/g, '');
  if (s.length !== 15) return 'VAT numbers in KSA are 15 digits';
  if (!s.startsWith('3') || !s.endsWith('3')) return 'KSA VAT numbers start and end with 3';
  return null;
}


export default function StepIdentity({ initialData, onSubmit, busy }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    // Hydrate when initialData arrives / changes. Defer to next tick
    // so the lint rule sees a non-synchronous state set.
    const h = setTimeout(() => {
      setForm({ ...(initialData || {}), address: { country_code: 'SA', ...(initialData?.address || {}) } });
    }, 0);
    return () => clearTimeout(h);
  }, [initialData]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setAddr = (key, value) =>
    setForm((f) => ({ ...f, address: { ...(f.address || { country_code: 'SA' }), [key]: value } }));

  const vatWarn = softVatNote(form.vat_number);
  const canSave = Boolean(form.name_ar || form.name_en);

  return (
    <div data-testid="step-identity" className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700">
          <Building2 size={18} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">Company identity</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Start with the legal foundation. Everything here flows into the new tenant&apos;s profile.
          </p>
        </div>
      </div>

      {FIELD_GROUPS.map((group) => (
        <section key={group.title} className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">{group.title}</h3>
            <p className="text-xs text-slate-400">{group.description}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {group.fields.map((f) => (
              <div key={f.key}>
                <label className="text-xs font-medium text-slate-500 mb-1 block">{f.label}</label>
                <Input
                  data-testid={`identity-${f.key}`}
                  type={f.type}
                  placeholder={f.placeholder}
                  dir={f.dir || 'ltr'}
                  maxLength={f.maxLength}
                  value={form[f.key] || ''}
                  onChange={(e) => set(f.key, e.target.value)}
                />
                {f.key === 'vat_number' && vatWarn && (
                  <div className="mt-1 text-[11px] text-amber-700 flex items-center gap-1">
                    <AlertTriangle size={10} /> {vatWarn}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}

      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">National address (KSA)</h3>
          <p className="text-xs text-slate-400">Matches the ZATCA national-address standard.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {ADDRESS_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-xs font-medium text-slate-500 mb-1 block">{f.label}</label>
              <Input
                data-testid={`identity-addr-${f.key}`}
                value={(form.address || {})[f.key] || ''}
                onChange={(e) => setAddr(f.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </section>

      <div className="flex items-center justify-end pt-2">
        <Button
          onClick={() => onSubmit(form)}
          disabled={!canSave || busy}
          data-testid="identity-save-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          Save & continue <ChevronRight size={14} className="ml-1.5" />
        </Button>
      </div>
      {!canSave && (
        <div className="text-xs text-amber-700 text-right">
          Provide at least the Arabic or English name to continue.
        </div>
      )}
    </div>
  );
}
